import React, { useState, useEffect, useMemo } from 'react';
import './HoldingsInsight.css';

const CRYPTO = new Set(['BTC','ETH','SOL','AVAX','NEAR','SUI','LINK','AAVE','ONDO','HYPE','WLD','TON','TIA','AKT','OP','RNDR','DOGE','XRP','ADA','DOT','MATIC','ATOM','UNI','MKR']);

export default function HoldingsInsight({ portfolioItems, analysisResult }) {
  const [collapsed, setCollapsed] = useState(false);
  const [sortKey, setSortKey] = useState('pnl_dollar');
  const [sortDir, setSortDir] = useState('desc');
  const [insightData, setInsightData] = useState({});
  const [insightLoading, setInsightLoading] = useState(false);

  // Fetch fair value, analyst, insider data from dedicated endpoint
  useEffect(() => {
    if (!portfolioItems || portfolioItems.length === 0) return;
    const equitySymbols = portfolioItems
      .map(p => p.symbol.toUpperCase())
      .filter(s => !CRYPTO.has(s));
    if (equitySymbols.length === 0) return;

    setInsightLoading(true);
    fetch(`/api/research/holdings-insight?symbols=${equitySymbols.join(',')}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.data) setInsightData(data.data);
      })
      .catch(() => {})
      .finally(() => setInsightLoading(false));
  }, [portfolioItems]);

  const holdings = useMemo(() => {
    if (!portfolioItems) return [];
    // Filter out digital assets — they don't have FMP fundamental data
    return portfolioItems.filter(p => !CRYPTO.has(p.symbol.toUpperCase())).map(p => {
      const sym = p.symbol.toUpperCase();
      const shares = p.shares || 0;
      const entry = p.entry_price || 0;
      const current = p.current_price || entry;
      const costBasis = shares * entry;
      const marketValue = shares * current;
      const pnlDollar = marketValue - costBasis;
      const pnlPct = entry > 0 ? ((current - entry) / entry) * 100 : 0;

      const insight = insightData[sym] || {};
      const fairValue = insight.fair_value || null;
      const fairValueUpside = fairValue && fairValue > 0 && current > 0
        ? ((fairValue - current) / current) * 100 : null;
      const priceTarget = insight.target_consensus || null;
      const ptUpside = priceTarget && current > 0
        ? ((priceTarget - current) / current) * 100 : null;
      const consensus = insight.consensus || null;
      const insiderBuys = insight.insider_buys || 0;
      const insiderSells = insight.insider_sells || 0;

      return {
        symbol: sym, shares, entry, current, costBasis, marketValue,
        pnl_dollar: pnlDollar, pnl_pct: pnlPct,
        fairValue, fairValueUpside, priceTarget, ptUpside, consensus,
        insiderBuys, insiderSells, isCrypto: CRYPTO.has(sym),
      };
    });
  }, [portfolioItems, insightData]);

  const sorted = useMemo(() => {
    return [...holdings].sort((a, b) => {
      const aVal = a[sortKey] ?? -Infinity;
      const bVal = b[sortKey] ?? -Infinity;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [holdings, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sortIcon = (key) => sortKey !== key ? '' : sortDir === 'desc' ? ' ▾' : ' ▴';

  if (holdings.length === 0) return null;

  const totalCost = holdings.reduce((s, h) => s + h.costBasis, 0);
  const totalValue = holdings.reduce((s, h) => s + h.marketValue, 0);
  const totalPnl = totalValue - totalCost;

  const fmt = (v) => v == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  const fmtPct = (v) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

  return (
    <div className="holdings-insight">
      <div className="hi-header">
        <h3 className="hi-title">Holdings Insight {insightLoading && <span className="hi-loading-badge">loading...</span>}</h3>
        <div className="hi-header-right">
          <div className="hi-total">
            <span className="hi-total-label">Total P&L</span>
            <span className={`hi-total-value ${totalPnl >= 0 ? 'positive' : 'negative'}`}>{fmt(totalPnl)}</span>
          </div>
          <button className="hi-toggle" onClick={() => setCollapsed(!collapsed)}>{collapsed ? 'Show' : 'Hide'}</button>
        </div>
      </div>

      {!collapsed && (
        <div className="hi-table-wrapper">
          <table className="hi-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th className="hi-sortable" onClick={() => handleSort('costBasis')}>Cost{sortIcon('costBasis')}</th>
                <th className="hi-sortable" onClick={() => handleSort('marketValue')}>Value{sortIcon('marketValue')}</th>
                <th className="hi-sortable" onClick={() => handleSort('pnl_dollar')}>P&L ${sortIcon('pnl_dollar')}</th>
                <th className="hi-sortable" onClick={() => handleSort('pnl_pct')}>P&L %{sortIcon('pnl_pct')}</th>
                <th className="hi-sortable" onClick={() => handleSort('fairValue')}>Fair Value{sortIcon('fairValue')}</th>
                <th className="hi-sortable" onClick={() => handleSort('priceTarget')}>Target{sortIcon('priceTarget')}</th>
                <th>Rating</th>
                <th>Insider</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((h) => (
                <tr key={h.symbol}>
                  <td className="hi-symbol">{h.symbol}</td>
                  <td className="hi-mono">{fmt(h.costBasis)}</td>
                  <td className="hi-mono">{fmt(h.marketValue)}</td>
                  <td className={`hi-mono ${h.pnl_dollar >= 0 ? 'positive' : 'negative'}`}>{fmt(h.pnl_dollar)}</td>
                  <td className={`hi-mono ${h.pnl_pct >= 0 ? 'positive' : 'negative'}`}>{fmtPct(h.pnl_pct)}</td>
                  <td>
                    {h.fairValue != null ? (
                      <div className="hi-value-cell">
                        <span className="hi-mono">${h.fairValue.toFixed(0)}</span>
                        <span className={`hi-upside ${h.fairValueUpside >= 0 ? 'positive' : 'negative'}`}>{fmtPct(h.fairValueUpside)}</span>
                      </div>
                    ) : <span className="hi-na">{h.isCrypto ? 'N/A' : '—'}</span>}
                  </td>
                  <td>
                    {h.priceTarget != null ? (
                      <div className="hi-value-cell">
                        <span className="hi-mono">${h.priceTarget.toFixed(0)}</span>
                        <span className={`hi-upside ${h.ptUpside >= 0 ? 'positive' : 'negative'}`}>{fmtPct(h.ptUpside)}</span>
                      </div>
                    ) : <span className="hi-na">{h.isCrypto ? 'N/A' : '—'}</span>}
                  </td>
                  <td>
                    {h.consensus ? (
                      <span className={`hi-consensus hi-consensus-${h.consensus.toLowerCase()}`}>{h.consensus}</span>
                    ) : <span className="hi-na">{h.isCrypto ? 'N/A' : '—'}</span>}
                  </td>
                  <td>
                    {(h.insiderBuys > 0 || h.insiderSells > 0) ? (
                      <div className="hi-insider">
                        {h.insiderBuys > 0 && <span className="hi-insider-buy">B:{h.insiderBuys}</span>}
                        {h.insiderSells > 0 && <span className="hi-insider-sell">S:{h.insiderSells}</span>}
                      </div>
                    ) : <span className="hi-na">{h.isCrypto ? 'N/A' : '—'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
